import { useEffect, useMemo, useState } from 'react';
import { ManagementOauthApi } from '../../../api/apis';
import { apiClient } from '../../../services/apiClient';
import { statusService } from '../../../services/statusService';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { Client, ClientWithDevices } from '../../../types/client';
import { isAbortError } from '../../../utils/abortError';
import { SortDirection, nextSort } from '../../common/SortableHeader';

export type ClientSortField = 'name' | 'deviceCount' | 'siren' | 'active' | 'lastActivity';

/** One line of the table: a root (depth 0) or one of its agencies (depth 1). */
export interface ClientRow extends ClientWithDevices {
  depth: 0 | 1;
  /** Roots: how many agencies they have, shown or not. */
  agencyCount: number;
  /** Roots: their own gateways plus their agencies'. */
  groupDeviceCount: number;
  rootName?: string;
}

/**
 * The clients list: every client with its fleet size (client list and
 * system status fetched together), then search, sort and paging here.
 * Agencies sit under their root, sorted by name, and fold away with it;
 * an agency whose root is out of scope is a line of its own.
 */
export const useClientsList = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { t } = useI18n();
  const managementApi = useMemo(() => new ManagementOauthApi(apiClient.getConfiguration()), []);

  const [clients, setClients] = useState<ClientWithDevices[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [sirenSearch, setSirenSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState<{ field: ClientSortField; direction: SortDirection }>({ field: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Client list and system status in parallel: the status call is the expensive one at fleet scale.
        const [rawClients, statusData] = await Promise.all([
          managementApi.apiGetClientListV2V2ClientGet({}),
          statusService.getSystemStatus({ signal: ac.signal }),
        ]);
        if (cancelled) return;
        const devicesByClient = new Map(statusData.clients_stats.map((stat) => [stat.client_id, stat.total_devices || 0]));
        const rows: ClientWithDevices[] = ((rawClients as any[]) || []).map((c: any) => {
          const client: Client = { ...c, clientName: c.clientName || c.name || '', name: c.clientName || c.name || '' };
          return { ...client, deviceCount: devicesByClient.get(client.id) || 0, lastActivity: new Date(client.updated_at) };
        });
        setClients(rows);
      } catch (err: any) {
        if (cancelled || isAbortError(err)) return;
        console.error('[CLIENTS TABLE] Error fetching clients:', err);
        setError(err.message || t('clients.errorLoading'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [managementApi, isAuthenticated, refreshTrigger, localRefresh, t]);

  const filteredSorted = useMemo<ClientRow[]>(() => {
    const needle = searchTerm.toLowerCase();
    const valid = clients.filter((client) => client?.id && client?.name);
    const byId = new Set(valid.map((c) => c.id));
    const textMatch = (client: ClientWithDevices) => {
      const matchesSearch = client.id.toLowerCase().includes(needle) || client.name.toLowerCase().includes(needle);
      const matchesSiren = !sirenSearch || Boolean(client.siren && client.siren.includes(sirenSearch));
      return matchesSearch && matchesSiren;
    };
    const statusOk = (client: ClientWithDevices) => ((showInactive && canSeeInactive) ? true : !!client.active);

    // Agencies under their root; a root is a line without a known parent.
    const agencies = new Map<string, ClientWithDevices[]>();
    valid.forEach((c) => {
      if (c.parent_id && byId.has(c.parent_id)) agencies.set(c.parent_id, [...(agencies.get(c.parent_id) || []), c]);
    });
    const roots = valid.filter((c) => !(c.parent_id && byId.has(c.parent_id)));

    const dir = sort.direction === 'desc' ? -1 : 1;
    const compare = (a: ClientWithDevices, b: ClientWithDevices) => {
      const av = a[sort.field];
      const bv = b[sort.field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    };

    const rows: ClientRow[] = [];
    roots
      .filter((root) => statusOk(root) && (textMatch(root) || (agencies.get(root.id) || []).some((a) => statusOk(a) && textMatch(a))))
      .sort(compare)
      .forEach((root) => {
        const all = agencies.get(root.id) || [];
        const shown = all.filter((a) => statusOk(a) && (textMatch(a) || textMatch(root))).sort((a, b) => a.name.localeCompare(b.name));
        rows.push({ ...root, depth: 0, agencyCount: all.length, groupDeviceCount: (root.deviceCount || 0) + all.reduce((s, a) => s + (a.deviceCount || 0), 0) });
        if (!collapsed.has(root.id)) shown.forEach((a) => rows.push({ ...a, depth: 1, agencyCount: 0, groupDeviceCount: a.deviceCount || 0, rootName: root.name }));
      });
    return rows;
  }, [clients, searchTerm, sirenSearch, showInactive, canSeeInactive, sort, collapsed]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / itemsPerPage));
  const pageRows = useMemo(() => filteredSorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [filteredSorted, currentPage, itemsPerPage]);

  const facts = useMemo(() => ({
    total: clients.length,
    active: clients.filter((c) => c.active).length,
    inactive: clients.filter((c) => !c.active).length,
    agencies: clients.filter((c) => c.parent_id || c.isAgency).length,
    devices: clients.reduce((sum, c) => sum + (c.deviceCount || 0), 0),
  }), [clients]);
  const maxDevices = useMemo(() => filteredSorted.reduce((m, c) => Math.max(m, c.agencyCount > 0 ? c.groupDeviceCount : (c.deviceCount || 0)), 0), [filteredSorted]);

  const isCollapsed = (id: string) => collapsed.has(id);
  const toggleCollapsed = (id: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const sortBy = (field: ClientSortField) => {
    setSort((s) => nextSort(s, field, ['deviceCount', 'lastActivity']));
    setCurrentPage(1);
  };

  return {
    canSeeInactive, loading, error, pageRows, allRows: clients, totalItems: filteredSorted.length, totalPages, facts, maxDevices,
    isCollapsed, toggleCollapsed,
    searchTerm, setSearchTerm, sirenSearch, setSirenSearch, showInactive, setShowInactive, hasFilters: Boolean(searchTerm || sirenSearch),
    sort, sortBy, currentPage, setCurrentPage, itemsPerPage, setItemsPerPage,
    reload: () => setLocalRefresh((n) => n + 1),
  };
};

export type ClientsListState = ReturnType<typeof useClientsList>;
